import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { FC, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import NumberFormat from "react-number-format";
import { PostOrderPayload } from "../../../../../types";
import { getValue, needMultiply } from "../../../../../utils/inputTransform";
import { PostOrderAPI } from "../../../../../utils/api/requests/postOrder-requests";
import { AxiosError } from "../../../../../utils/api/axiosErrorType";
import { getAddPostOrderShema } from "../../../../../utils/schemas/PostOrderShemas";
import { tagManagerEvent } from "../../../../../utils/tagManagerEvent";
import { theme } from "../../../../../theme";
import { useTranslation } from "react-i18next";
// import { Select } from "../../../../../ui/atoms/Select";

interface AddPostOrderModalProps {
  open: boolean;
  handleClose: () => void;
  refetch: () => void;
}

export const AddPostOrderModal: FC<AddPostOrderModalProps> = ({
  open,
  handleClose,
  refetch,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const form = useForm<PostOrderPayload>({
    mode: "onChange",
    resolver: joiResolver(getAddPostOrderShema()),
  });
  const { handleSubmit, formState, control, reset } = form;

  const { isDirty, isValid } = formState;

  const { mutate, isLoading } = useMutation({
    mutationFn: PostOrderAPI.addPostOrder,
    onSuccess: () => {
      refetch();
      toast.success(t("posts.addPostModal.postAdded"));
      handleClose();
    },
    onError: (error) => {
      const err = error as AxiosError;
      toast.error(err?.response?.data?.detail);
    },
  });

  const onSubmit = async (formData: PostOrderPayload) => {
    tagManagerEvent("Add_order", "Add_order_button");
    mutate(formData);
  };

  // const viewsModeOptions = [
  //   { value: "CUSTOM", label: "Custom" },
  //   { value: "ORGANIC", label: "Organic" },
  // ];

  useEffect(() => {
    // if (open) {
    //   reset({ views_mode: "CUSTOM" });
    // }
    if (!open) {
      reset();
    }
  }, [open, reset]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={isMobile}
    >
      <FormProvider {...form}>
        <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle id="alert-dialog-title">
            {t("posts.addPostModal.header")}
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "sm", sm: "350px", md: "350px" },
            }}
          >
            <Controller
              control={control}
              name="post_link"
              defaultValue=""
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  label={t("posts.addPostModal.postLink")}
                  variant="outlined"
                  autoComplete="off"
                  sx={{
                    marginTop: "12px",
                  }}
                  error={!!error}
                />
              )}
            />
            <Controller
              control={control}
              name="views"
              render={({
                field: { ref, onChange, value, ...field },
                fieldState: { error },
              }) => (
                <NumberFormat
                  customInput={TextField}
                  inputRef={ref}
                  onValueChange={needMultiply(1, onChange)}
                  value={getValue(value, true, 1)}
                  type="text"
                  variant="outlined"
                  label={t("posts.addPostModal.viewsCount")}
                  error={!!error}
                  fullWidth={true}
                  decimalScale={0}
                  autoComplete="off"
                  allowNegative={false}
                  sx={{
                    marginTop: "12px",
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name="speed"
              render={({
                field: { ref, onChange, value, ...field },
                fieldState: { error },
              }) => (
                <NumberFormat
                  customInput={TextField}
                  inputRef={ref}
                  onValueChange={needMultiply(1, onChange)}
                  value={getValue(value, true, 1)}
                  type="text"
                  variant="outlined"
                  label={t("posts.speed")}
                  error={!!error}
                  fullWidth={true}
                  decimalScale={0}
                  allowNegative={false}
                  sx={{
                    marginTop: "12px",
                    mb: "12px",
                  }}
                  autoComplete="off"
                />
              )}
            />
            {/* <Select
              name={"views_mode"}
              isEditing={true}
              data={viewsModeOptions}
              placeholder="Views mode"
              sx={{ mt: "12px" }}
              fullWidth={true}
            /> */}
            <Controller
            name="include_other_sources"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={t("includeOtherSources")}
              />
            )}
          />
          </DialogContent>
          <DialogActions sx={{ marginBottom: "10px", px: "24px" }}>
            <Button variant="outlined" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!isDirty || !isValid || isLoading}
            >
              {t("submit")}
            </Button>
          </DialogActions>
        </Box>
      </FormProvider>
    </Dialog>
  );
};
